import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  FolderPlus,
  Edit,
  Trash2,
  Folder,
  User
} from "lucide-react";
import { candidateFoldersAPI } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ModernBadge, ModernLoading, ModernPaginationFooter } from "@/components/common/ModernPageTemplate";

export default function FolderManagementModal({
  isOpen,
  onClose,
  onCreateFolder,
  onEditFolder,
  onFolderCreatedOrEdited
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [createdByFilter, setCreatedByFilter] = useState("all");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, folder: null });
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [isPaginationEnabled, setIsPaginationEnabled] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { toast } = useToast();
  const limit = rowsPerPage; // Use rowsPerPage for limit

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['folders-management-modal', { page, limit, searchTerm, createdByFilter, refetchTrigger, isPaginationEnabled }],
    queryFn: () => candidateFoldersAPI.getAll({
      page: isPaginationEnabled ? page : 1,
      limit: isPaginationEnabled ? limit : 999999,
      search: searchTerm,
      createdBy: createdByFilter
    }),
    enabled: isOpen,
    keepPreviousData: true, // keeps old data while fetching new
    refetchOnMount: true, // Always refetch when modal opens
  });

  // Refetch folders when modal opens and reset to page 1
  useEffect(() => {
    if (isOpen) {
      setPage(1); // Reset to first page when modal opens
      setSearchTerm(""); // Clear search
      refetch();
    }
  }, [isOpen, refetch]);

  // Function to trigger refetch from outside
  const triggerRefetch = () => {
    setPage(1); // Reset to page 1
    setRefetchTrigger(prev => prev + 1); // Change trigger to force refetch
    if (onFolderCreatedOrEdited) {
      onFolderCreatedOrEdited();
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1); // Reset to first page when searching
  };

  const handleDeleteFolder = async (folderId, confirmation) => {
    try {
      await candidateFoldersAPI.delete(folderId, confirmation);
      toast({
        title: "Folder deleted",
        description: "The folder has been deleted successfully and candidates moved to Others",
      });
      setDeleteDialog({ open: false, folder: null });
      triggerRefetch(); // Refetch folders in modal after deletion
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete folder",
        variant: "destructive",
      });
    }
  };

  // Function to handle folder creation - pass refetch callback
  const handleCreateFolderSuccess = () => {
    onCreateFolder(triggerRefetch);
  };

  // Function to handle folder edit - pass refetch callback
  const handleEditFolderSuccess = (folder) => {
    onEditFolder(folder, triggerRefetch);
  };

  const folders = data?.data?.data || [];
  const pagination = data?.data?.pagination || {};

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Folder className="h-5 w-5" />
              Folder Management
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Header with all controls in one line */}
            <div className="flex items-center gap-3">
              {/* Create Folder Icon Button */}
              <Button 
                onClick={handleCreateFolderSuccess} 
                size="sm"
                variant="outline"
                className="admin-action-btn"
                title="Create Folder"
              >
                <FolderPlus className="w-4 h-4" />
              </Button>

              {/* Search Bar */}
              <div className="flex-1">
                <div className="admin-search-input-wrapper-header">
                  <input
                    type="search"
                    placeholder="Search folders by name..."
                    className="admin-search-input-header"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearch(e);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="admin-search-icon-header-clickable"
                    onClick={handleSearch}
                    title="Search"
                  >
                    <Search className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Filter Dropdown */}
              <Select value={createdByFilter} onValueChange={(value) => { setCreatedByFilter(value); setPage(1); }}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Folders</SelectItem>
                  <SelectItem value="me">My Folders</SelectItem>
                  <SelectItem value="company">Company Folders</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Folder Table */}
            <div className="min-h-[400px]">
              {isLoading ? (
                <ModernLoading text="Loading folders..." />
              ) : folders.length > 0 ? (
                <div className="admin-table-container" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                  <table className="admin-table">
                    <thead className="admin-table-header" style={{ position: 'sticky', top: 0, zIndex: 1, background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
                      <tr>
                        <th className="admin-table-cell">S.No</th>
                        <th className="admin-table-cell">Folder Name</th>
                        <th className="admin-table-cell">Description</th>
                        <th className="admin-table-cell">Total Candidates</th>
                        <th className="admin-table-cell">Created By</th>
                        <th className="admin-table-cell">Created At</th>
                        <th className="admin-table-cell text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {folders.map((folder, index) => (
                        <tr key={folder._id} className="admin-table-row">
                          <td className="admin-table-cell number">
                            {isPaginationEnabled ? (page - 1) * limit + index + 1 : index + 1}
                          </td>
                          <td className="admin-table-cell font-medium">
                            <div className="flex items-center">
                              <Folder
                                className="w-4 h-4 mr-2 flex-shrink-0"
                                style={{ color: folder.color || "#3882F6" }}
                              />
                              <span className="text-sm">{folder.name || "Unnamed Folder"}</span>
                            </div>
                          </td>
                          <td className="admin-table-cell">
                            <span className="text-sm text-gray-600">
                              {folder.description || "No description"}
                            </span>
                          </td>
                          <td className="admin-table-cell">
                            <ModernBadge variant="language">
                              <span className="text-xs">
                                {folder.candidateCount || 0} candidate{folder.candidateCount !== 1 && "s"}
                              </span>
                            </ModernBadge>
                          </td>
                          <td className="admin-table-cell">
                            {folder.createdBy ? (
                              <ModernBadge variant="template">
                                <span className="text-xs">{folder.createdBy.name}</span>
                              </ModernBadge>
                            ) : (
                              <span className="text-gray-400 text-xs">N/A</span>
                            )}
                          </td>
                          <td className="admin-table-cell">
                            <span className="text-xs">
                              {format(new Date(folder.createdAt), "MMM dd, yyyy")}
                            </span>
                          </td>
                          <td className="admin-table-cell text-right">
                            <div className="admin-table-actions">
                              <button
                                className="admin-table-action-btn edit"
                                onClick={() => handleEditFolderSuccess(folder)}
                                title="Edit folder"
                              >
                                <Edit className="h-3 w-3" />
                              </button>
                              <button
                                className="admin-table-action-btn delete"
                                onClick={() => setDeleteDialog({ open: true, folder })}
                                title="Delete folder"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center p-12 text-gray-500">
                  <Folder className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No folders found</p>
                  <p className="text-sm">Create your first folder to organize candidates</p>
                </div>
              )}
            </div>

            {/* Modern Pagination Footer */}
            <ModernPaginationFooter
              isPaginationEnabled={isPaginationEnabled}
              onPaginationToggle={setIsPaginationEnabled}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(value) => {
                setRowsPerPage(parseInt(value));
                setPage(1);
              }}
              pagination={{
                current: page,
                pages: pagination.totalPages || 1,
                total: pagination.total || folders.length
              }}
              onPageChange={setPage}
              rowsOptions={[5, 10, 20, 50]}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, folder: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Folder?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the folder "{deleteDialog.folder?.name}"?
              {deleteDialog.folder?.candidateCount > 0 && (
                <span className="block mt-2 text-orange-600 font-medium">
                  This folder contains {deleteDialog.folder.candidateCount} candidate(s). 
                  They will be moved to "Others" folder.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialog({ open: false, folder: null })}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteFolder(deleteDialog.folder?._id, true)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}